import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Divider, Drawer, Fab, IconButton, Stack, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import AssistantComposer from './AssistantComposer';
import MessageBubble from './MessageBubble';
import { warmHostAssistant } from '../../../services/host-assistant-client';
import { suggestionsForTab } from './suggestions';
import { useAskTripsby } from './useAskTripsby';

interface Props {
  groupId: string;
  /** The active GroupDetail tab id ("guests", "rooms", …). */
  tab?: string;
}

export const ASSISTANT_FOOTER = "Ask Tripsby can read this group's details. It won't change anything or message guests.";

/**
 * "Ask Tripsby" — read-only Q&A for the host, as a side panel on the group page.
 *
 * Phase 1 only ever asks questions: the one thing this panel can call is
 * streamHostAssistant, plus the best-effort warmHostAssistant that readies the backend
 * and changes nothing (see assistant.contract.test.ts). Conversation state lives
 * here, not inside the Drawer, so closing the panel mid-answer does not cancel it.
 */
function AskTripsbyPanelForGroup({ groupId, tab }: Props) {
  const [open, setOpen] = useState(false);
  const { messages, pending, status, error, send, retry, stop, reset } = useAskTripsby(groupId, tab);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // The first question after a cold start pays ~25 s of backend warm-up unless it was asked for early.
  // Opening the panel asks (see also the launcher's hover/focus below); the client sends it at most once
  // per page session, ignores any failure, and shows nothing.
  useEffect(() => {
    if (open) warmHostAssistant();
  }, [open]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, pending, status, error, open]);

  const lastIsUser = messages.length > 0 && messages[messages.length - 1].role === 'user';

  return (
    <>
      <Fab
        variant="extended"
        color="primary"
        size="medium"
        aria-label="Ask Tripsby"
        onClick={() => setOpen(true)}
        // Reaching for the launcher is the earliest signal that a question is coming.
        onMouseEnter={warmHostAssistant}
        onFocus={warmHostAssistant}
        sx={{ position: 'fixed', right: 16, bottom: 16, '@media print': { display: 'none' } }}
      >
        <AutoAwesomeIcon sx={{ mr: 1 }} />
        Ask Tripsby
      </Fab>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)} // Esc and backdrop click
        // Focus the question box once the slide-in finishes; the modal's own focus
        // trap would otherwise leave focus on the panel itself.
        SlideProps={{ onEntered: () => inputRef.current?.focus() }}
        PaperProps={{
          role: 'dialog',
          'aria-label': 'Ask Tripsby',
          sx: { width: { xs: '100%', sm: 420 }, maxWidth: '100%', display: 'flex', flexDirection: 'column' },
        }}
        sx={{ '@media print': { display: 'none' } }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5 }}>
          <AutoAwesomeIcon color="primary" fontSize="small" />
          <Typography variant="h6" component="h2" sx={{ flex: 1 }}>
            Ask Tripsby
          </Typography>
          <Button size="small" onClick={reset} disabled={messages.length === 0 && !pending && !error}>
            New conversation
          </Button>
          <IconButton aria-label="Close Ask Tripsby" onClick={() => setOpen(false)} edge="end">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider />

        <Box
          ref={listRef}
          role="log"
          aria-live="polite"
          aria-label="Conversation"
          sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}
        >
          {messages.length === 0 && !pending && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Ask about your guests, rooms, dinners, seating or broadcasts — or how to do something in Studio.
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {suggestionsForTab(tab).map((q) => (
                  <Chip key={q} label={q} clickable variant="outlined" color="primary" onClick={() => send(q)} />
                ))}
              </Stack>
            </Box>
          )}
          {messages.map((m) => (
            <MessageBubble key={m.id} message={m} />
          ))}
          {pending && (
            <Stack direction="row" spacing={1} alignItems="center" data-testid="assistant-status">
              <CircularProgress size={14} aria-hidden />
              <Typography variant="body2" color="text.secondary">
                {status ?? 'Thinking…'}
              </Typography>
            </Stack>
          )}
          {error && (
            <Alert
              severity={error.code === 'aborted' ? 'info' : 'warning'}
              role="alert"
              action={
                error.retryable && lastIsUser ? (
                  <Button color="inherit" size="small" onClick={retry}>
                    Retry
                  </Button>
                ) : undefined
              }
            >
              {error.message}
            </Alert>
          )}
        </Box>

        <Divider />
        <Box sx={{ p: 2 }}>
          <AssistantComposer pending={pending} inputRef={inputRef} onSend={send} onStop={stop} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {ASSISTANT_FOOTER}
          </Typography>
        </Box>
      </Drawer>
    </>
  );
}

/** Keyed by group so a conversation can never carry over to a different group. */
export default function AskTripsbyPanel(props: Props) {
  return <AskTripsbyPanelForGroup key={props.groupId} {...props} />;
}
